import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  LogOut, Plus, School as SchoolIcon, Shield, Upload, X, Pencil, Send, ExternalLink,
  PowerOff, Power, Users, Trash2, History, ChevronDown, ChevronRight, XCircle, Mail,
} from 'lucide-react';

const BOARDS = ['CBSE', 'ICSE', 'State Board', 'IB', 'Cambridge', 'Other'];
const DEFAULT_BRAND_COLOR = '#4F46E5';
const MAX_LOGO_BYTES = 500 * 1024;
const MAX_BG_BYTES = 2 * 1024 * 1024;

const EMPTY_FORM = {
  name: '',
  slug: '',
  board: '',
  admin_email: '',     // required for create — becomes invite recipient
  contact_email: '',
  logo_url: '',
  login_bg_url: '',
  brand_color: DEFAULT_BRAND_COLOR,
};

/**
 * Platform Admin dashboard — schools management.
 * Ticket 1A.4: branding uploads + auto-invite on create + edit dialog + re-invite.
 */
export default function PlatformAdmin() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogMode, setDialogMode] = useState(null); // 'create' | 'edit' | null
  const [editingSchool, setEditingSchool] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [logoFile, setLogoFile] = useState(null);
  const [bgFile, setBgFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reinvitingId, setReinvitingId] = useState(null);
  const [userCounts, setUserCounts] = useState({});         // {school_id: {school_admins, teachers, students, parents, total}}
  const [togglingId, setTogglingId] = useState(null);       // school_id being deactivated/activated
  const [confirmToggle, setConfirmToggle] = useState(null); // { school, action: 'deactivate' | 'activate' }
  const [confirmDelete, setConfirmDelete] = useState(null); // { school }
  const [deleteSlugInput, setDeleteSlugInput] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [expandedInvitesId, setExpandedInvitesId] = useState(null); // school_id whose invites are expanded
  const [pendingInvitesBySchool, setPendingInvitesBySchool] = useState({}); // { school_id: invite[] }
  const [invitesLoadingId, setInvitesLoadingId] = useState(null);
  const [confirmRevokeInvite, setConfirmRevokeInvite] = useState(null); // invite object
  const [revokingInviteId, setRevokingInviteId] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [meRes, schoolsRes] = await Promise.all([
        api.get('/auth/me'),
        api.get('/schools'),
      ]);
      setMe(meRes.data);
      const schoolList = schoolsRes.data || [];
      setSchools(schoolList);

      // Fan-out fetch of per-school user counts (best-effort, non-blocking on individual fails).
      const counts = {};
      await Promise.all(
        schoolList.map(async (s) => {
          try {
            const { data } = await api.get(`/schools/${s.school_id}/user-counts`);
            counts[s.school_id] = data;
          } catch { /* skip — card shows "—" */ }
        }),
      );
      setUserCounts(counts);
    } catch {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const handleLogout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    localStorage.removeItem('yukti_session_token');
    navigate('/', { replace: true });
  };

  // --- Dialog open/close helpers ---
  const openCreate = () => {
    setEditingSchool(null);
    setForm(EMPTY_FORM);
    setLogoFile(null);
    setBgFile(null);
    setDialogMode('create');
  };

  const openEdit = (school) => {
    setEditingSchool(school);
    setForm({
      name: school.name || '',
      slug: school.slug || '',
      board: school.board || '',
      admin_email: '',  // not shown in edit mode
      contact_email: school.contact_email || '',
      logo_url: school.logo_url || '',
      login_bg_url: school.login_bg_url || '',
      brand_color: school.brand_color || DEFAULT_BRAND_COLOR,
    });
    setLogoFile(null);
    setBgFile(null);
    setDialogMode('edit');
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogMode(null);
    setEditingSchool(null);
    setLogoFile(null);
    setBgFile(null);
  };

  // --- Asset upload helper ---
  const uploadAsset = async (file, kind, schoolId) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    fd.append('school_id', schoolId);
    const { data } = await api.post('/schools/upload-asset', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.url;
  };

  // --- Create submit ---
  const submitCreate = async () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('School name must be at least 2 characters'); return;
    }
    if (!form.admin_email.trim() || !form.admin_email.includes('@')) {
      toast.error('School Admin Email is required'); return;
    }
    if (logoFile && logoFile.size > MAX_LOGO_BYTES) {
      toast.error('Logo is larger than 500 KB'); return;
    }
    if (bgFile && bgFile.size > MAX_BG_BYTES) {
      toast.error('Background image is larger than 2 MB'); return;
    }

    setSaving(true);
    try {
      // 1. Create school (without asset URLs yet)
      const createPayload = {
        name: form.name.trim(),
        ...(form.slug.trim() ? { slug: form.slug.trim() } : {}),
        ...(form.board ? { board: form.board } : {}),
        ...(form.contact_email.trim() ? { contact_email: form.contact_email.trim() } : {}),
        ...(form.brand_color ? { brand_color: form.brand_color } : {}),
      };
      const { data: created } = await api.post('/schools', createPayload);
      const schoolId = created.school_id;

      // 2. Upload assets (if any) and patch the school
      const assetUpdates = {};
      if (logoFile) assetUpdates.logo_url = await uploadAsset(logoFile, 'logo', schoolId);
      if (bgFile) assetUpdates.login_bg_url = await uploadAsset(bgFile, 'login-bg', schoolId);
      if (Object.keys(assetUpdates).length > 0) {
        await api.patch(`/schools/${schoolId}`, assetUpdates);
      }

      // 3. Send admin invite
      try {
        await api.post(`/schools/${schoolId}/invites`, { email: form.admin_email.trim().toLowerCase() });
        toast.success(`Created ${created.name} and invited ${form.admin_email.trim()}`);
      } catch (inviteErr) {
        const detail = inviteErr?.response?.data?.detail || 'School created, but invite failed to send.';
        toast.warning(detail);
      }

      closeDialog();
      await loadAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to create school';
      toast.error(typeof msg === 'string' ? msg : 'Failed to create school');
    } finally {
      setSaving(false);
    }
  };

  // --- Edit submit ---
  const submitEdit = async () => {
    if (!editingSchool) return;
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error('School name must be at least 2 characters'); return;
    }
    if (logoFile && logoFile.size > MAX_LOGO_BYTES) {
      toast.error('Logo is larger than 500 KB'); return;
    }
    if (bgFile && bgFile.size > MAX_BG_BYTES) {
      toast.error('Background image is larger than 2 MB'); return;
    }

    setSaving(true);
    try {
      const schoolId = editingSchool.school_id;

      // Upload any new assets first
      let logoUrl = form.logo_url || null;
      let bgUrl = form.login_bg_url || null;
      if (logoFile) logoUrl = await uploadAsset(logoFile, 'logo', schoolId);
      if (bgFile) bgUrl = await uploadAsset(bgFile, 'login-bg', schoolId);

      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || editingSchool.slug,
        board: form.board || null,
        contact_email: form.contact_email.trim() || null,
        brand_color: form.brand_color || null,
        logo_url: logoUrl,
        login_bg_url: bgUrl,
      };

      await api.patch(`/schools/${schoolId}`, payload);
      toast.success(`Updated ${payload.name}`);
      closeDialog();
      await loadAll();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Failed to update school';
      toast.error(typeof msg === 'string' ? msg : 'Failed to update school');
    } finally {
      setSaving(false);
    }
  };

  // --- Re-invite ---
  const handleReinvite = async (school) => {
    const email = window.prompt(
      `Send a new invite for ${school.name}. Enter the School Admin email:`,
      school.contact_email || '',
    );
    if (!email || !email.includes('@')) return;
    setReinvitingId(school.school_id);
    try {
      await api.post(`/schools/${school.school_id}/invites`, { email: email.trim().toLowerCase() });
      toast.success(`Invite sent to ${email.trim()}`);
      // If the expanded panel is open for this school, refresh it
      if (expandedInvitesId === school.school_id) {
        await loadPendingInvites(school.school_id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send invite');
    } finally {
      setReinvitingId(null);
    }
  };

  // --- Pending invites panel ---
  const loadPendingInvites = async (schoolId) => {
    setInvitesLoadingId(schoolId);
    try {
      const { data } = await api.get(`/schools/${schoolId}/invites?status=pending`);
      setPendingInvitesBySchool((prev) => ({ ...prev, [schoolId]: data?.items || [] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load invites');
    } finally {
      setInvitesLoadingId(null);
    }
  };

  const toggleInvitesPanel = async (schoolId) => {
    if (expandedInvitesId === schoolId) {
      setExpandedInvitesId(null);
      return;
    }
    setExpandedInvitesId(schoolId);
    if (!pendingInvitesBySchool[schoolId]) {
      await loadPendingInvites(schoolId);
    }
  };

  const handleRevokeInvite = async () => {
    if (!confirmRevokeInvite) return;
    setRevokingInviteId(confirmRevokeInvite.invite_id);
    try {
      await api.post(`/invites/${confirmRevokeInvite.invite_id}/revoke`);
      toast.success(`Invite for ${confirmRevokeInvite.email} revoked.`);
      const schoolId = confirmRevokeInvite.school_id || expandedInvitesId;
      setConfirmRevokeInvite(null);
      if (schoolId) await loadPendingInvites(schoolId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke invite');
    } finally {
      setRevokingInviteId(null);
    }
  };

  // --- Deactivate / Activate ---
  const requestToggle = (school) => {
    const action = school.status === 'deactivated' ? 'activate' : 'deactivate';
    setConfirmToggle({ school, action });
  };

  const handleToggle = async () => {
    if (!confirmToggle) return;
    const { school, action } = confirmToggle;
    setTogglingId(school.school_id);
    try {
      const { data } = await api.patch(`/schools/${school.school_id}/${action}`);
      // Optimistic local replace
      setSchools((prev) => prev.map((s) => s.school_id === school.school_id ? data : s));
      toast.success(
        action === 'deactivate'
          ? `${school.name} has been deactivated. All school users have been logged out.`
          : `${school.name} has been re-activated. Users can log in again.`,
      );
      setConfirmToggle(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Failed to ${action} school`);
    } finally {
      setTogglingId(null);
    }
  };

  // --- Hard delete (Slice 3) ---
  const requestDelete = (school) => {
    setDeleteSlugInput("");
    setConfirmDelete({ school });
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const { school } = confirmDelete;
    if (deleteSlugInput.trim() !== school.slug) {
      toast.error(`Type the school slug exactly: "${school.slug}"`);
      return;
    }
    setDeletingId(school.school_id);
    try {
      const { data } = await api.delete(`/schools/${school.school_id}`, {
        params: { confirm_slug: school.slug },
      });
      setSchools((prev) => prev.filter((s) => s.school_id !== school.school_id));
      const c = data.counts || {};
      toast.success(
        `${school.name} deleted. Removed ${c.users || 0} users, ` +
        `${c.school_classes || 0} classes, ${c.assessments || 0} assessments, ` +
        `${c.children || 0} child profiles.`,
        { duration: 6000 },
      );
      setConfirmDelete(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete school");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50"
      data-testid="platform-admin-page"
    >
      {/* Top bar */}
      <header className="border-b bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center">
              <Shield size={18} />
            </div>
            <div>
              <h1 className="font-bold text-slate-900" data-testid="platform-admin-title">
                Yukti · Platform Admin
              </h1>
              <p className="text-xs text-slate-500">{me?.email || '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost" size="sm"
              onClick={() => navigate('/platform-admin/audit-log')}
              data-testid="platform-admin-audit-log-btn"
            >
              <History size={16} className="mr-1.5" /> Audit Log
            </Button>
            <Button
              variant="ghost" size="sm" onClick={handleLogout}
              data-testid="platform-admin-logout-btn"
            >
              <LogOut size={16} className="mr-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <section data-testid="schools-section">
          <div className="flex items-end justify-between mb-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <SchoolIcon size={18} className="text-indigo-600" />
                <h2 className="text-xl font-bold text-slate-900">Schools</h2>
              </div>
              <p className="text-sm text-slate-600">
                Create schools, upload branding, and invite school admins.
              </p>
            </div>
            <Button
              onClick={openCreate}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="create-school-btn"
            >
              <Plus size={16} className="mr-1.5" />
              New School
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-16 text-slate-500" data-testid="schools-loading">
              Loading…
            </div>
          ) : schools.length === 0 ? (
            <div
              className="border-2 border-dashed rounded-xl py-16 text-center bg-white/60"
              data-testid="schools-empty-state"
            >
              <SchoolIcon size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-600 font-medium">No schools yet</p>
              <p className="text-sm text-slate-500 mt-1">
                Click "New School" to onboard the first one.
              </p>
            </div>
          ) : (
            <div className="grid gap-3" data-testid="schools-list">
              {schools.map((s) => {
                const isDeactivated = s.status === 'deactivated';
                const counts = userCounts[s.school_id];
                const invitesOpen = expandedInvitesId === s.school_id;
                const schoolInvites = pendingInvitesBySchool[s.school_id];
                return (
                <div
                  key={s.school_id}
                  className={`bg-white border rounded-xl hover:shadow-sm transition ${isDeactivated ? 'opacity-75 border-amber-200 bg-amber-50/30' : ''}`}
                  data-testid={`school-card-${s.slug}`}
                >
                  <div className="flex items-center justify-between gap-4 px-5 py-4">
                  <div className="flex items-center gap-4 min-w-0 flex-1">
                    {s.logo_url ? (
                      <img
                        src={s.logo_url}
                        alt={s.name}
                        className="w-12 h-12 rounded-lg object-contain bg-slate-50 border flex-shrink-0"
                      />
                    ) : (
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0"
                        style={{ backgroundColor: s.brand_color || DEFAULT_BRAND_COLOR }}
                      >
                        <SchoolIcon size={20} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 truncate">{s.name}</span>
                        {isDeactivated && (
                          <span
                            className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-200 text-amber-900"
                            data-testid={`school-status-deactivated-${s.slug}`}
                          >
                            Deactivated
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-x-3">
                        <span className="font-mono">/{s.slug}</span>
                        {s.board && <span>· {s.board}</span>}
                        {s.contact_email && <span>· {s.contact_email}</span>}
                        {s.brand_color && (
                          <span className="flex items-center gap-1">
                            ·
                            <span
                              className="inline-block w-3 h-3 rounded border border-slate-200"
                              style={{ backgroundColor: s.brand_color }}
                            />
                            {s.brand_color}
                          </span>
                        )}
                      </div>
                      {/* User counts strip (Slice 1) */}
                      <div
                        className="mt-2 flex items-center gap-3 text-xs text-slate-600 flex-wrap"
                        data-testid={`school-user-counts-${s.slug}`}
                      >
                        <Users size={12} className="text-slate-400" />
                        {counts ? (
                          <>
                            <span><b className="text-slate-800">{counts.school_admins}</b> admins</span>
                            <span className="text-slate-300">·</span>
                            <span><b className="text-slate-800">{counts.teachers}</b> teachers</span>
                            <span className="text-slate-300">·</span>
                            <span><b className="text-slate-800">{counts.students}</b> students</span>
                            <span className="text-slate-300">·</span>
                            <span><b className="text-slate-800">{counts.parents}</b> parents</span>
                          </>
                        ) : (
                          <span className="text-slate-400 italic">loading user counts…</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => window.open(`/school/${s.slug}/login`, '_blank', 'noopener,noreferrer')}
                      data-testid={`school-preview-${s.slug}`}
                      title="Open the live school login page"
                    >
                      <ExternalLink size={14} className="mr-1" />
                      Preview
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => handleReinvite(s)}
                      disabled={reinvitingId === s.school_id || isDeactivated}
                      data-testid={`school-reinvite-${s.slug}`}
                      title={isDeactivated ? 'Reactivate the school before re-inviting' : 'Send a new school-admin invite'}
                    >
                      <Send size={14} className="mr-1" />
                      Invite
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => openEdit(s)}
                      data-testid={`school-edit-${s.slug}`}
                    >
                      <Pencil size={14} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => navigate(`/platform-admin/schools/${s.school_id}/users`)}
                      data-testid={`school-manage-users-${s.slug}`}
                      title="Browse and delete users in this school"
                    >
                      <Users size={14} className="mr-1" />
                      Users
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => toggleInvitesPanel(s.school_id)}
                      data-testid={`school-invites-toggle-${s.slug}`}
                      title="View and revoke pending invites for this school"
                    >
                      {invitesOpen ? <ChevronDown size={14} className="mr-1" /> : <ChevronRight size={14} className="mr-1" />}
                      <Mail size={14} className="mr-1" />
                      Invites
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => requestToggle(s)}
                      disabled={togglingId === s.school_id}
                      data-testid={`school-toggle-status-${s.slug}`}
                      title={isDeactivated ? 'Reactivate the school' : 'Deactivate the school'}
                      className={isDeactivated ? 'text-emerald-700 border-emerald-300 hover:bg-emerald-50' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}
                    >
                      {isDeactivated ? <Power size={14} className="mr-1" /> : <PowerOff size={14} className="mr-1" />}
                      {isDeactivated ? 'Activate' : 'Deactivate'}
                    </Button>
                    <Button
                      variant="outline" size="sm"
                      onClick={() => requestDelete(s)}
                      disabled={deletingId === s.school_id}
                      data-testid={`school-delete-${s.slug}`}
                      title="Permanently delete this school"
                      className="text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  </div>

                  {invitesOpen && (
                    <div
                      className="border-t bg-slate-50/60 px-5 py-4"
                      data-testid={`school-invites-panel-${s.slug}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                          Pending invites
                        </h4>
                        {invitesLoadingId === s.school_id && (
                          <span className="text-xs text-slate-400">Loading…</span>
                        )}
                      </div>
                      {!schoolInvites || schoolInvites.length === 0 ? (
                        <div className="text-sm text-slate-500 italic">
                          {invitesLoadingId === s.school_id ? 'Loading…' : 'No pending invites.'}
                        </div>
                      ) : (
                        <div className="grid gap-2">
                          {schoolInvites.map((inv) => (
                            <div
                              key={inv.invite_id}
                              className="bg-white border border-amber-200 rounded-lg px-3 py-2 flex items-center justify-between gap-3"
                              data-testid={`platform-pending-invite-${inv.invite_id}`}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <Mail size={14} className="text-amber-600 flex-shrink-0" />
                                <div className="min-w-0">
                                  <div className="text-sm font-medium text-slate-900 truncate">
                                    {inv.name || inv.email}
                                  </div>
                                  <div className="text-xs text-slate-500 truncate">
                                    {inv.email} · <span className="capitalize">{inv.role.replace('_', ' ')}</span>
                                    {inv.expires_at && (
                                      <> · expires {new Date(inv.expires_at).toLocaleDateString()}</>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="ghost" size="sm"
                                onClick={() => setConfirmRevokeInvite({ ...inv, school_id: s.school_id })}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                data-testid={`platform-invite-revoke-${inv.invite_id}`}
                                title="Revoke this pending invite"
                              >
                                <XCircle size={14} className="mr-1" /> Revoke
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
        </section>

      </main>

      {/* Create / Edit dialog */}
      <Dialog open={dialogMode !== null} onOpenChange={(v) => { if (!v) closeDialog(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="school-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'edit' ? `Edit ${editingSchool?.name}` : 'New School'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'edit'
                ? 'Update school details and branding. Only platform admins can edit schools.'
                : 'Create a school and send an invite to the school admin. Upload branding to customize the login page.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="school-name">School Name *</Label>
              <Input
                id="school-name"
                data-testid="school-name-input"
                placeholder="e.g., Delhi Public School, R.K. Puram"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                maxLength={120}
              />
            </div>

            <div>
              <Label htmlFor="school-slug">Slug {dialogMode === 'edit' ? '' : '(optional)'}</Label>
              <Input
                id="school-slug"
                data-testid="school-slug-input"
                placeholder="auto-generated from name if empty"
                value={form.slug}
                onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value.toLowerCase() }))}
                maxLength={50}
              />
              <p className="text-xs text-slate-500 mt-1">
                Used for the branded login URL: <span className="font-mono">/school/&lt;slug&gt;</span>
              </p>
            </div>

            <div>
              <Label htmlFor="school-board">Board</Label>
              <Select
                value={form.board}
                onValueChange={(v) => setForm((f) => ({ ...f, board: v }))}
              >
                <SelectTrigger id="school-board" data-testid="school-board-select">
                  <SelectValue placeholder="Select board" />
                </SelectTrigger>
                <SelectContent>
                  {BOARDS.map((b) => (
                    <SelectItem key={b} value={b} data-testid={`school-board-${b}`}>
                      {b}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {dialogMode === 'create' && (
              <div>
                <Label htmlFor="school-admin-email">School Admin Email *</Label>
                <Input
                  id="school-admin-email"
                  data-testid="school-admin-email-input"
                  type="email"
                  placeholder="admin@school.edu"
                  value={form.admin_email}
                  onChange={(e) => setForm((f) => ({ ...f, admin_email: e.target.value }))}
                  maxLength={200}
                />
                <p className="text-xs text-slate-500 mt-1">
                  We'll email this person an invite link to accept admin access.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="school-contact">Contact Email (optional)</Label>
              <Input
                id="school-contact"
                data-testid="school-contact-input"
                type="email"
                placeholder="hello@school.edu"
                value={form.contact_email}
                onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                maxLength={200}
              />
            </div>

            <div>
              <Label>Brand Color</Label>
              <div className="flex items-center gap-3 mt-1">
                <input
                  type="color"
                  data-testid="school-brand-color-picker"
                  value={form.brand_color || DEFAULT_BRAND_COLOR}
                  onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value.toUpperCase() }))}
                  className="h-10 w-14 rounded border border-slate-200 cursor-pointer"
                />
                <Input
                  data-testid="school-brand-color-input"
                  value={form.brand_color || ''}
                  onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value.toUpperCase() }))}
                  placeholder="#4F46E5"
                  maxLength={7}
                  className="font-mono"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Used on the school login page buttons and email invite.
              </p>
            </div>

            <AssetUploader
              label="School Logo"
              hint="PNG, JPG, SVG, or WebP · max 500 KB"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              maxBytes={MAX_LOGO_BYTES}
              currentUrl={form.logo_url}
              stagedFile={logoFile}
              onFile={setLogoFile}
              onClear={() => { setLogoFile(null); setForm((f) => ({ ...f, logo_url: '' })); }}
              testId="school-logo-uploader"
            />

            <AssetUploader
              label="Login Background"
              hint="JPG, PNG, or WebP · max 2 MB"
              accept="image/png,image/jpeg,image/webp"
              maxBytes={MAX_BG_BYTES}
              currentUrl={form.login_bg_url}
              stagedFile={bgFile}
              onFile={setBgFile}
              onClear={() => { setBgFile(null); setForm((f) => ({ ...f, login_bg_url: '' })); }}
              testId="school-bg-uploader"
              tall
            />
          </div>

          <DialogFooter>
            {dialogMode === 'edit' && form.slug && (
              <Button
                variant="outline"
                onClick={() => window.open(`/school/${form.slug}/login`, '_blank', 'noopener,noreferrer')}
                disabled={saving}
                data-testid="school-dialog-preview-btn"
                className="mr-auto"
              >
                <ExternalLink size={14} className="mr-1" />
                Preview
              </Button>
            )}
            <Button
              variant="outline" onClick={closeDialog} disabled={saving}
              data-testid="school-dialog-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={dialogMode === 'edit' ? submitEdit : submitCreate}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
              data-testid="school-dialog-submit-btn"
            >
              {saving
                ? (dialogMode === 'edit' ? 'Saving…' : 'Creating…')
                : (dialogMode === 'edit' ? 'Save Changes' : 'Create & Send Invite')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Deactivate / Activate dialog (Slice 1) */}
      <Dialog
        open={confirmToggle !== null}
        onOpenChange={(v) => { if (!v) setConfirmToggle(null); }}
      >
        <DialogContent className="max-w-md" data-testid="school-toggle-status-dialog">
          {confirmToggle && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {confirmToggle.action === 'deactivate'
                    ? `Deactivate ${confirmToggle.school.name}?`
                    : `Reactivate ${confirmToggle.school.name}?`}
                </DialogTitle>
                <DialogDescription>
                  {confirmToggle.action === 'deactivate' ? (
                    <>
                      All <b>{userCounts[confirmToggle.school.school_id]?.total ?? '—'}</b> users at this school will be
                      immediately logged out and unable to sign in. Their data is preserved
                      and you can reactivate at any time. The branded login page will show a
                      <i> School Currently Inactive</i> message.
                    </>
                  ) : (
                    <>
                      Users at <b>{confirmToggle.school.name}</b> will be able to log in again. No data is lost during
                      deactivation, so they'll resume right where they left off.
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmToggle(null)}
                  disabled={togglingId === confirmToggle.school.school_id}
                  data-testid="school-toggle-status-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleToggle}
                  disabled={togglingId === confirmToggle.school.school_id}
                  data-testid="school-toggle-status-confirm"
                  className={confirmToggle.action === 'deactivate' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                >
                  {togglingId === confirmToggle.school.school_id
                    ? 'Working…'
                    : confirmToggle.action === 'deactivate' ? 'Deactivate' : 'Reactivate'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm Hard-Delete dialog (Slice 3) — typed slug confirmation */}
      <Dialog
        open={confirmDelete !== null}
        onOpenChange={(v) => { if (!v) { setConfirmDelete(null); setDeleteSlugInput(""); } }}
      >
        <DialogContent className="max-w-md" data-testid="school-delete-dialog">
          {confirmDelete && (
            <>
              <DialogHeader>
                <DialogTitle className="text-red-700">
                  Delete {confirmDelete.school.name}?
                </DialogTitle>
                <DialogDescription>
                  This will <b>permanently remove</b> all data linked to this school.
                </DialogDescription>
              </DialogHeader>
              <div className="text-sm text-slate-700 space-y-2">
                <ul className="list-disc pl-5 space-y-0.5 text-xs">
                  <li>{userCounts[confirmDelete.school.school_id]?.total ?? '—'} users (admins, teachers, students, parents)</li>
                  <li>All classes, assignments, enrollments, assessments and attempts</li>
                  <li>All student chats, messages, mind-maps and progress</li>
                  <li>School logo, branding assets and pending invites</li>
                </ul>
                <div className="text-red-700 font-medium">This cannot be undone.</div>
                <div className="text-slate-700">
                  Type the school slug to confirm: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-900 font-mono">{confirmDelete.school.slug}</code>
                </div>
              </div>
              <Input
                placeholder={confirmDelete.school.slug}
                value={deleteSlugInput}
                onChange={(e) => setDeleteSlugInput(e.target.value)}
                className="font-mono"
                autoFocus
                data-testid="school-delete-slug-input"
              />
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => { setConfirmDelete(null); setDeleteSlugInput(""); }}
                  disabled={deletingId === confirmDelete.school.school_id}
                  data-testid="school-delete-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDelete}
                  disabled={
                    deletingId === confirmDelete.school.school_id ||
                    deleteSlugInput.trim() !== confirmDelete.school.slug
                  }
                  data-testid="school-delete-confirm"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  {deletingId === confirmDelete.school.school_id ? 'Deleting…' : 'Delete forever'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revoke pending invite (Platform Admin) */}
      <Dialog
        open={confirmRevokeInvite !== null}
        onOpenChange={(v) => { if (!v) setConfirmRevokeInvite(null); }}
      >
        <DialogContent className="max-w-md" data-testid="platform-invite-revoke-dialog">
          {confirmRevokeInvite && (
            <>
              <DialogHeader>
                <DialogTitle>Revoke {confirmRevokeInvite.role?.replace('_', ' ')} invite?</DialogTitle>
                <DialogDescription>
                  The link emailed to <b>{confirmRevokeInvite.email}</b> will stop working.
                  They won't be able to accept anymore. You can issue a fresh invite later.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setConfirmRevokeInvite(null)}
                  disabled={revokingInviteId === confirmRevokeInvite.invite_id}
                  data-testid="platform-invite-revoke-cancel"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRevokeInvite}
                  disabled={revokingInviteId === confirmRevokeInvite.invite_id}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="platform-invite-revoke-confirm"
                >
                  {revokingInviteId === confirmRevokeInvite.invite_id ? 'Revoking…' : 'Revoke invite'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Reusable asset uploader with live preview + remove + file-size guard.
 */
function AssetUploader({
  label, hint, accept, maxBytes, currentUrl, stagedFile, onFile, onClear, testId, tall,
}) {
  const inputRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (!stagedFile) { setPreviewUrl(null); return undefined; }
    const url = URL.createObjectURL(stagedFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [stagedFile]);

  const displayUrl = previewUrl || currentUrl || null;

  const handleChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > maxBytes) {
      toast.error(`File too large. Max ${Math.round(maxBytes / 1024)} KB.`);
      return;
    }
    onFile(file);
  };

  return (
    <div data-testid={testId}>
      <Label>{label}</Label>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="hidden"
        data-testid={`${testId}-input`}
      />
      {displayUrl ? (
        <div className={`relative mt-1 border rounded-lg overflow-hidden bg-slate-50 ${tall ? 'h-40' : 'h-24'}`}>
          <img
            src={displayUrl}
            alt={label}
            className="w-full h-full object-contain"
            data-testid={`${testId}-preview`}
          />
          <div className="absolute top-2 right-2 flex gap-1">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="bg-white/90 hover:bg-white rounded-md px-2 py-1 text-xs font-medium shadow"
              data-testid={`${testId}-replace`}
            >
              Replace
            </button>
            <button
              type="button"
              onClick={onClear}
              className="bg-white/90 hover:bg-white rounded-md p-1 shadow text-slate-600"
              data-testid={`${testId}-remove`}
              aria-label="Remove"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`mt-1 w-full ${tall ? 'h-40' : 'h-24'} border-2 border-dashed border-slate-200 rounded-lg flex flex-col items-center justify-center gap-1 hover:border-indigo-300 hover:bg-indigo-50/40 transition`}
          data-testid={`${testId}-trigger`}
        >
          <Upload size={18} className="text-slate-400" />
          <span className="text-sm text-slate-600 font-medium">Click to upload</span>
          <span className="text-xs text-slate-400">{hint}</span>
        </button>
      )}
    </div>
  );
}
